import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ArrowRight, ArrowLeft, Loader2, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BrandMark } from "../components/BrandMark";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";

export default function Login() {
  const { login, loading, user, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState("google");
  const [mobileEnabled, setMobileEnabled] = useState(false);

  // Mobile flow state
  const [step, setStep] = useState("phone");         // phone | code
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // Password (manager) flow state
  const [pwEmail, setPwEmail] = useState("");
  const [pwPassword, setPwPassword] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const passwordLogin = async (e) => {
    e?.preventDefault();
    if (!pwEmail || !pwPassword) return;
    setPwBusy(true);
    try {
      const { data } = await api.post("/auth/password-login", { email: pwEmail, password: pwPassword });
      if (data?.session_token) localStorage.setItem("pr_session_token", data.session_token);
      toast.success("Welcome back");
      // Password login is the manager sign-in flow. If the account has a staff/admin role,
      // ALWAYS land in the Back Office — ignore any lingering location.state.from that may
      // point at a customer-side page.
      const role = data?.user?.role;
      const dest = (role === "staff" || role === "admin")
        ? "/backoffice"
        : (location.state?.from || "/backoffice");
      // NOTE: Do NOT call setUser() here — that would trigger a re-render
      // of Login where the top-level `!loading && user` guard would race
      // this replace() and force us back to "/". Instead, hard-navigate
      // and let AuthProvider re-fetch /auth/me on the next page load
      // (session token is already in localStorage for the bearer fallback).
      window.location.replace(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in");
    } finally {
      setPwBusy(false);
    }
  };

  useEffect(() => {
    api.get("/auth/mobile/status")
      .then((r) => setMobileEnabled(!!r.data.enabled))
      .catch(() => setMobileEnabled(false));
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  if (!loading && user) {
    // Route staff/admin to Back Office by default, customers to home.
    const roleDefault = (user.role === "staff" || user.role === "admin") ? "/backoffice" : "/";
    const from = location.state?.from || roleDefault;
    window.location.replace(from);
    return null;
  }

  const formatPhoneInput = (raw) => {
    // Live-format US as (XXX) XXX-XXXX
    const d = raw.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      toast.error("Enter a 10-digit US or Canadian mobile number");
      return;
    }
    setSending(true);
    try {
      await api.post("/auth/mobile/send-code", { phone });
      setStep("code");
      setResendIn(30);
      toast.success("Code sent — check your phone");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/mobile/verify-code", { phone, code, name });
      if (data?.session_token) localStorage.setItem("pr_session_token", data.session_token);
      if (data?.user) setUser(data.user);
      toast.success("Welcome to Plenty Rydez");
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired code");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <section
      data-testid={TID.auth.loginPage}
      className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-5"
    >
      <div className="glass rounded-3xl p-8 sm:p-10 max-w-md w-full text-center noise-overlay relative overflow-hidden">
        <div className="flex justify-center mb-6"><BrandMark size={56} /></div>
        <div className="label-caps text-burgundy mb-2">Members only</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-3">Sign in to Plenty Rydez</h1>
        <p className="text-white/60 mb-8 leading-relaxed text-sm">
          Choose how you’d like to sign in.
        </p>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-full glass mb-6">
          <button
            data-testid="login-tab-google"
            onClick={() => setTab("google")}
            className={`flex-1 py-2 rounded-full text-xs label-caps transition-colors ${
              tab === "google" ? "bg-burgundy text-white" : "text-white/60 hover:text-white"
            }`}
          >
            Google
          </button>
          <button
            data-testid="login-tab-mobile"
            onClick={() => setTab("mobile")}
            disabled={!mobileEnabled}
            title={mobileEnabled ? "Sign in with your phone" : "Mobile login not configured yet"}
            className={`flex-1 py-2 rounded-full text-xs label-caps transition-colors ${
              tab === "mobile" ? "bg-burgundy text-white" : "text-white/60 hover:text-white"
            } ${!mobileEnabled && "opacity-40 cursor-not-allowed"}`}
          >
            <Phone size={12} className="inline mr-1" /> Mobile
          </button>
        </div>

        {tab === "google" && (
          <>
            <button
              data-testid={TID.auth.loginBtn}
              onClick={login}
              className="btn-burgundy w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>
            {!mobileEnabled && (
              <p className="text-[11px] text-white/40 mt-4">
                Prefer mobile? Ask an admin to add Twilio credentials in <code>backend/.env</code>.
              </p>
            )}
          </>
        )}

        {tab === "mobile" && (
          <>
            {step === "phone" && (
              <form onSubmit={sendCode} className="space-y-4 text-left">
                <label className="block">
                  <div className="label-caps text-white/60 mb-2">Full name <span className="text-white/30">(optional, first time only)</span></div>
                  <input
                    data-testid="login-mobile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Jane Rider"
                    className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-3 focus:border-burgundy outline-none"
                  />
                </label>
                <label className="block">
                  <div className="label-caps text-white/60 mb-2">Mobile number (US · Canada)</div>
                  <div className="flex items-center bg-ink-800 border border-white/10 rounded-xl focus-within:border-burgundy">
                    <span className="px-4 text-white/50 border-r border-white/10">+1</span>
                    <input
                      data-testid="login-mobile-phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                      placeholder="(212) 555-0142"
                      className="flex-1 bg-transparent px-4 py-3 outline-none"
                      required
                    />
                  </div>
                </label>
                <button
                  data-testid="login-mobile-send"
                  type="submit"
                  disabled={sending}
                  className="btn-burgundy w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {sending ? "Sending code…" : "Text me a code"}
                </button>
                <p className="text-[11px] text-white/40 text-center">
                  Standard message and data rates may apply. We’ll only text you sign-in codes.
                </p>
              </form>
            )}

            {step === "code" && (
              <form onSubmit={verifyCode} className="space-y-4 text-left">
                <button type="button" onClick={() => { setStep("phone"); setCode(""); }}
                  className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1">
                  <ArrowLeft size={12} /> Use a different number
                </button>
                <div className="label-caps text-white/60 mt-2">Enter the 6-digit code sent to +1 {phone}</div>
                <input
                  data-testid="login-mobile-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] font-serif focus:border-burgundy outline-none"
                  required
                />
                <button
                  data-testid="login-mobile-verify"
                  type="submit"
                  disabled={verifying || code.length !== 6}
                  className="btn-burgundy w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {verifying ? <Loader2 size={16} className="animate-spin" /> : null}
                  {verifying ? "Verifying…" : "Verify & sign in"}
                </button>
                <div className="text-center text-xs text-white/50">
                  {resendIn > 0 ? (
                    <>Didn’t get it? Resend in {resendIn}s</>
                  ) : (
                    <button type="button" onClick={sendCode} className="text-burgundy hover:underline">
                      Resend code
                    </button>
                  )}
                </div>
              </form>
            )}
          </>
        )}

        {tab === "manager" && (
          <form onSubmit={passwordLogin} className="space-y-4 text-left">
            <div className="label-caps text-electric text-[10px] text-center mb-2">
              Back Office · owners &amp; managers only
            </div>
            <label className="block">
              <div className="label-caps text-white/60 mb-2">Email</div>
              <input
                data-testid="login-pw-email"
                type="email"
                value={pwEmail}
                onChange={(e) => setPwEmail(e.target.value)}
                placeholder="you@plenty-rydez.com"
                className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-3 focus:border-burgundy outline-none"
                autoComplete="email"
                required
              />
            </label>
            <label className="block">
              <div className="label-caps text-white/60 mb-2">Password</div>
              <input
                data-testid="login-pw-password"
                type="password"
                value={pwPassword}
                onChange={(e) => setPwPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-3 focus:border-burgundy outline-none"
                autoComplete="current-password"
                required
                minLength={8}
              />
            </label>
            <button
              data-testid="login-pw-submit"
              type="submit"
              disabled={pwBusy}
              className="btn-burgundy w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {pwBusy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {pwBusy ? "Signing in…" : "Sign in to Back Office"}
            </button>
            <p className="text-[11px] text-white/40 text-center">
              Need access? Ask an owner to send you an invite from the Team tab of the Back Office.
            </p>
          </form>
        )}

        <div className="mt-8 text-xs text-white/40">
          By signing in you agree to our terms of service and privacy policy.
        </div>

        <a
          href="/manager-login"
          data-testid="login-mgr-link"
          className="mt-6 flex items-center justify-between gap-3 p-4 rounded-2xl border border-electric/40 bg-electric/5 hover:bg-electric/15 hover:border-electric transition group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-electric/20 border border-electric/40 flex items-center justify-center">
              <KeyRound size={18} className="text-electric" />
            </div>
            <div className="text-left">
              <div className="label-caps text-electric text-[10px]">Staff · Owner · Manager</div>
              <div className="text-sm text-white font-serif">Back Office sign-in</div>
            </div>
          </div>
          <ArrowRight size={16} className="text-electric group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </section>
  );
}
